import { useState } from 'react';

interface EmailSubscribeResponse {
  processMessage: string;
  processSubscribe: (
    email: string,
    postalcode: string,
    ListIds: number[],
    params?: Record<string, string>
  ) => Promise<void>;
  hasError: boolean;
  isSuccessful: boolean;
}

interface ApiResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export default function useEmailFormSubmit(): EmailSubscribeResponse {
  const [processMessage, setProcessMessage] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const processSubscribe = async (
    email: string,
    postalcode: string,
    ListIds: number[],
    params?: Record<string, string>
  ) => {
    try {
      // Reset states
      setProcessMessage('');
      setHasError(false);
      setIsSuccessful(false);

      // Validate inputs
      if (!email) throw new Error('Email is required');
      if (!ListIds?.length)
        throw new Error('Please select at least one newsletter');

      const response = await fetch('/api/newsletters/updatesubscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          postalcode: postalcode || '',
          ListIds: ListIds.join(','),
          params,
        }),
      });

      const data = (await response.json()) as ApiResponse;

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to subscribe to newsletter');
      }

      setProcessMessage(data.message || 'Thank you for subscribing!');
      setHasError(false);
      setIsSuccessful(true);

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'gtm.formSubmit',
          gtm: {
            elementUrl: 'https://www.mprnews.org/newsletter',
          },
        });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setProcessMessage(
        error instanceof Error
          ? error.message
          : 'An error occurred while processing your subscription'
      );
      setHasError(true);
      setIsSuccessful(false);
    }
  };

  return {
    processMessage,
    processSubscribe,
    hasError,
    isSuccessful,
  };
}
